import { DatePicker, Input, Modal } from 'antd';
import moment, { Moment } from 'moment';
import React from 'react';
import { Button } from '../../button';
import style from './style.module.scss';

export const NOTE_MAX_LENGTH = 128;

// Matches the Figma: "30 sep. 2024 9:00 AM".
const VISIT_DATE_FORMAT = 'D MMM YYYY h:mm A';

const range = (from: number, to: number) => {
    const values = [];
    for (let value = from; value < to; value++) {
        values.push(value);
    }
    return values;
};

/**
 * `disabledDate` only rules out whole days, so on today it still let an agent
 * pick an hour that has not happened. The API refuses a future timestamp, but
 * the picker should not offer it in the first place.
 */
export const disabledFutureTime = (current: Moment | null) => {
    const now = moment();
    if (!current || !current.isSame(now, 'day')) {
        return {};
    }
    return {
        disabledHours: () => range(now.hour() + 1, 24),
        disabledMinutes: (hour: number) =>
            hour === now.hour() ? range(now.minute() + 1, 60) : [],
    };
};

type AddVisitModalProps = {
    open: boolean;
    isSaving: boolean;
    onCancel: () => void;
    onSubmit: (values: {
        occurredAt: string;
        note: string | null;
        requestKey: string;
    }) => void;
};

export const AddVisitModal = ({
    open,
    isSaving,
    onCancel,
    onSubmit,
}: AddVisitModalProps) => {
    const [note, setNote] = React.useState('');
    const [occurredAt, setOccurredAt] = React.useState<Moment | null>(null);
    // One key per opened modal, sent with every submit of it. A retry after a
    // dropped response reuses it, so the ledger recognises the replay and
    // refuses it instead of crediting a second visit.
    const [requestKey, setRequestKey] = React.useState('');

    // Belt and braces. Ant Design already refuses a typed value that fails
    // `disabledDate`, so nothing reaches this through the UI today — it is here
    // so a future change to the pickers cannot quietly start sending the API a
    // timestamp it will reject.
    const isFuture = Boolean(occurredAt && occurredAt.isAfter(moment()));

    // Reopening starts clean: a note left over from a previous client would be
    // attributed to this one.
    React.useEffect(() => {
        if (open) {
            setNote('');
            setOccurredAt(moment());
            setRequestKey(crypto.randomUUID());
        }
    }, [open]);

    return (
        <Modal
            title="Client visit"
            open={open}
            onCancel={onCancel}
            destroyOnClose
            footer={
                <div className={style.modal_footer}>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button
                        variant="primary"
                        loading={isSaving}
                        disabled={!occurredAt || isFuture}
                        onClick={() =>
                            occurredAt &&
                            onSubmit({
                                occurredAt: occurredAt.toISOString(),
                                note: note.trim() || null,
                                requestKey,
                            })
                        }
                    >
                        Add visit
                    </Button>
                </div>
            }
        >
            <div className={style.modal_field}>
                <label htmlFor="cs-visit-note">Note (optional)</label>
                <Input
                    id="cs-visit-note"
                    placeholder="Receipt, reason or comment"
                    value={note}
                    maxLength={NOTE_MAX_LENGTH}
                    showCount
                    onChange={(event) => setNote(event.target.value)}
                />
            </div>

            <div className={style.modal_field}>
                <label htmlFor="cs-visit-date">Visit date and time</label>
                <DatePicker
                    id="cs-visit-date"
                    className={style.date_picker}
                    value={occurredAt}
                    showTime={{ use12Hours: true, format: 'h:mm A' }}
                    format={VISIT_DATE_FORMAT}
                    // A visit that has not happened yet cannot be credited; the
                    // API refuses it too, this just says so sooner.
                    disabledDate={(current) =>
                        current && current > moment().endOf('day')
                    }
                    disabledTime={disabledFutureTime}
                    onChange={setOccurredAt}
                />
            </div>
        </Modal>
    );
};
